import {
  AdminSubpath,
  AutotractorSubpath,
  EquipmentSubpath,
  FleetSubpath,
  Path,
  ReportSubpath,
  RobotSubpath,
  Slug,
} from "portal/utils/routing";
import { postHog } from "portal/utils/postHog";
import { useEffect, useMemo } from "react";
import { useLocation, useMatches } from "react-router-dom";
import { UserDisplayRole, userDisplayRoleToJSON } from "protos/portal/auth";
import { useSelf } from "portal/state/store";

// Fallback segments not in this set are reported as `:param`, so unknown
// routes lose detail instead of leaking record ids.
const KNOWN_SEGMENTS = new Set(
  [
    ...Object.values(Path),
    ...Object.values(RobotSubpath),
    ...Object.values(EquipmentSubpath),
    ...Object.values(AutotractorSubpath),
    ...Object.values(FleetSubpath),
    ...Object.values(ReportSubpath),
    ...Object.values(AdminSubpath),
    ...Object.values(Slug),
  ]
    .flatMap((path) => path.split("/"))
    .filter((segment) => segment && segment !== "*" && !segment.startsWith(":"))
);

// React Router hands back resolved pathnames; swap the resolved params back out
// so `/robots/L0123` and `/robots/L0456` report as one route.
export const toRoutePattern = (
  pathname: string,
  parameters: Record<string, string | undefined>
): string => {
  const { "*": splat, ...named } = parameters;
  let segments = pathname.split("/").filter(Boolean);
  // `useMatches()` cannot see nested `useRoutes()` screens; drop the splat
  // remainder entirely so nested record ids never leak.
  const splatLength = splat?.split("/").filter(Boolean).length ?? 0;
  if (splatLength > 0) {
    segments = segments.slice(0, Math.max(0, segments.length - splatLength));
  }
  const namedParameters = Object.entries(named).filter(
    (entry): entry is [string, string] => Boolean(entry[1])
  );
  // Match whole segments only; params are decoded, pathnames may not be.
  return `/${segments
    .map((segment) => {
      const parameter = namedParameters.find(
        ([, value]) =>
          value === segment || encodeURIComponent(value) === segment
      );
      if (parameter) {
        return `:${parameter[0]}`;
      }
      return KNOWN_SEGMENTS.has(segment) ? segment : ":param";
    })
    .join("/")}`;
};

// Keep the portal's route casing and hyphenated segments readable while
// removing path and parameter separators.
export const toScreenName = (routePattern: string): string =>
  routePattern.replaceAll(/[/_:]+/g, " ").trim();

/** Identifies the signed-in user and reports route views to PostHog. */
export const usePostHog = (): void => {
  const { user, isInternal, isLoading } = useSelf();
  const matches = useMatches();
  const { pathname } = useLocation();

  const userId = user?.userId;
  const role = userDisplayRoleToJSON(
    user?.appMetadata?.role ?? UserDisplayRole.unknown_role
  );
  const customerUuid = user?.appMetadata?.customerUuid;
  useEffect(() => {
    if (isLoading || !userId) {
      return;
    }
    postHog.identify({ userId, role, isInternal, customerUuid });
  }, [customerUuid, isInternal, isLoading, role, userId]);

  // `matches` is a fresh array every render, so depend on the derived pattern.
  const routePattern = useMemo(() => {
    const match = matches.at(-1);
    return match ? toRoutePattern(match.pathname, match.params) : undefined;
  }, [matches]);
  useEffect(() => {
    // Identification is declared in the effect above, so React runs it first
    // when self finishes loading. This also avoids capturing transient auth
    // routes such as `/callback/` while the signed-in identity is unresolved.
    if (isLoading || !userId || !routePattern) {
      return;
    }
    postHog.page(toScreenName(routePattern), { route_pattern: routePattern });
    // `pathname` only triggers capture so `/farms/a` → `/farms/b` counts twice;
    // query-only changes do not.
  }, [isLoading, pathname, routePattern, userId]);
};
